/**
 * 配色とレイアウトのトークン（NFR-W4）。
 *
 * 値は `ais-nozomi/packages/ui/stories/foundations/*.mdx` のデザイン基盤に合わせている。
 * 色は **役割（primary / destructive …）で参照**し、生の色名は書かない。
 *
 * ★ ダークの切り替えは `prefers-color-scheme`。基盤の規約は `.dark` クラスだが、
 * この画面には切り替え UI が無く、端末の設定に従うのが素直。
 *
 * CSS ファイルを別に持たず 1 箇所の文字列にまとめてある（ビルド構成を増やさないため）。
 */
export const GLOBAL_CSS = `
:root {
  color-scheme: light dark;

  /* ── 色（oklch。役割で参照する）─────────────────────────── */
  --background: oklch(1 0 0);
  --foreground: oklch(0.147 0.004 49.25);
  --card: oklch(1 0 0);
  --card-foreground: oklch(0.147 0.004 49.25);
  --popover: oklch(1 0 0);
  --popover-foreground: oklch(0.147 0.004 49.25);
  --primary: oklch(0.52 0.105 223.128);
  --primary-foreground: oklch(0.984 0.019 200.873);
  --secondary: oklch(0.967 0.001 286.375);
  --secondary-foreground: oklch(0.21 0.006 285.885);
  /*
   * ★ --accent は**面**の色（ホバーの背景）。文字には使わない。
   * 明 0.97 / 暗 0.268 なので、そのまま文字にすると
   * どちらのモードでも背景に溶けて読めなくなる。
   */
  --accent: oklch(0.97 0.001 106.424);
  --accent-foreground: oklch(0.216 0.006 56.043);
  /* 強調して読ませる文字（リンク・印）。面ではなく文字のための色。 */
  --link: oklch(0.45 0.12 223.128);
  --muted: oklch(0.97 0.001 106.424);
  /*
   * ★ 実測 4.39:1 だったので少し暗くした（AA は 4.5:1）。
   * 補助文はどの画面にも大量に出るので、ここが足りないと全体が読みにくい。
   */
  --muted-foreground: oklch(0.52 0.013 58.071);
  --destructive: oklch(0.577 0.245 27.325);
  --destructive-foreground: oklch(0.985 0 0);
  --success: oklch(0.527 0.154 150.069);
  --success-foreground: oklch(0.985 0 0);
  /*
   * ★ 明るい琥珀は白地の文字にすると 2.13:1 で読めなかった。
   * この色は .badge.warn の**文字**にしか使っていないので、
   * 用途に合う明度まで落とす（基盤の値からの意図的なずれ）。
   */
  --warning: oklch(0.56 0.15 70.08);
  --warning-foreground: oklch(0.414 0.112 45.904);
  --border: oklch(0.923 0.003 48.717);
  --input: oklch(0.923 0.003 48.717);
  --ring: oklch(0.709 0.01 56.259);
  --sidebar: oklch(0.985 0.001 106.423);
  --sidebar-foreground: oklch(0.147 0.004 49.25);

  /* JSON 編集欄の構文色。ライト／ダークで別に持つ（固定色だと片方で読めない）。 */
  --syn-key: oklch(0.55 0.2 300);
  --syn-string: oklch(0.5 0.13 155);
  --syn-number: oklch(0.55 0.14 55);
  --syn-literal: oklch(0.52 0.15 255);
  --syn-punct: oklch(0.553 0.013 58.071);

  /* ── レイアウト定数（4px グリッド）───────────────────────── */
  --radius: 0.5rem;
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
  --sidebar-width: 240px;
  --nav-height: 48px;
  --container-max: 1400px;
  /*
   * 表の行の下限。
   *
   * ★ 表の中のボタン（32px）＋上下の余白（12px ずつ）が収まる高さにする。
   * 足りないと、ボタンのある行だけ背が高くなって縦に揃わない。
   * box-sizing: border-box なので、この値に余白を含む。
   */
  --row-height: 56px;
}

/* ダーク。端末設定と、明示的な .dark の両方で効かせる。 */
@media (prefers-color-scheme: dark) {
  :root:not(.light) {
    --background: oklch(0.147 0.004 49.25);
    --foreground: oklch(0.985 0.001 106.423);
    --card: oklch(0.216 0.006 56.043);
    --card-foreground: oklch(0.985 0.001 106.423);
    --popover: oklch(0.216 0.006 56.043);
    --popover-foreground: oklch(0.985 0.001 106.423);
    --primary: oklch(0.45 0.085 224.283);
    --primary-foreground: oklch(0.984 0.019 200.873);
    --secondary: oklch(0.274 0.006 286.033);
    --secondary-foreground: oklch(0.985 0 0);
    --accent: oklch(0.268 0.007 34.298);
    --accent-foreground: oklch(0.985 0.001 106.423);
    --link: oklch(0.78 0.11 223.128);
    --muted: oklch(0.268 0.007 34.298);
    --muted-foreground: oklch(0.709 0.01 56.259);
    --destructive: oklch(0.704 0.191 22.216);
    --destructive-foreground: oklch(0.985 0 0);
    --success: oklch(0.596 0.145 149.214);
    --success-foreground: oklch(0.985 0 0);
    --warning: oklch(0.828 0.189 84.429);
    --warning-foreground: oklch(0.962 0.059 95.617);
    --border: oklch(1 0 0 / 10%);
    --input: oklch(1 0 0 / 15%);
    --ring: oklch(0.553 0.013 58.071);
    --sidebar: oklch(0.216 0.006 56.043);
    --sidebar-foreground: oklch(0.985 0.001 106.423);

    --syn-key: oklch(0.8 0.13 300);
    --syn-string: oklch(0.85 0.14 155);
    --syn-number: oklch(0.82 0.12 60);
    --syn-literal: oklch(0.78 0.11 255);
    --syn-punct: oklch(0.709 0.01 56.259);
  }
}

* { box-sizing: border-box; }

html, body, #root {
  height: 100%;
}

body {
  margin: 0;
  background: var(--background);
  color: var(--foreground);
  font-family: Inter, -apple-system, BlinkMacSystemFont, Arial,
    "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Noto Sans JP", sans-serif;
  font-size: 14px;
  /* 日本語の可読性のため行間は広めに取る。 */
  line-height: 1.75;
}

/* 見出しの階層。密度のために読みやすさを削らない。 */
h1, h2, h3, h4 { letter-spacing: -0.02em; margin: 0; }
h1 { font-size: 20px; font-weight: 700; }
h2 { font-size: 18px; font-weight: 600; }
h3 { font-size: 16px; font-weight: 600; }
h4 { font-size: 14px; font-weight: 600; }

:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

/**
 * 本文へ飛ぶ道。
 *
 * ★ **焦点が当たるまで見えない。** 常に出すと、見えている人には
 * 意味のない札が居座る。
 *
 * ★ **display none にしない。** それだと並びから外れて、キーボードでも
 * 辿り着けない——出すためのものが出なくなる。
 *
 * ★ この注釈にバッククォートを書かないこと。この面は CSS を
 * テンプレートリテラルで持っているので、そこで文字列が切れる（4 回目）。
 */
.skip-link {
  position: absolute;
  left: 8px;
  top: -48px;
  z-index: 1000;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--card);
  color: var(--foreground);
  border: 1px solid var(--border);
  text-decoration: none;
  transition: top 120ms ease-out;
}

.skip-link:focus-visible {
  top: 8px;
}

/* ★ 動きを減らす設定なら、滑らせずに出す。 */
@media (prefers-reduced-motion: reduce) {
  .skip-link {
    transition: none;
  }
}

button {
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: inherit;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  /* タッチ領域は最低 40px。日本語ラベルが読める高さを確保する。 */
  min-height: 40px;
  padding: 8px 14px;
  cursor: pointer;
}
button:hover:not(:disabled) { background: var(--accent); color: var(--accent-foreground); }
button:disabled { opacity: 0.5; pointer-events: none; }
button.primary {
  background: var(--primary);
  color: var(--primary-foreground);
  border-color: transparent;
}
button.primary:hover:not(:disabled) {
  background: var(--primary);
  color: var(--primary-foreground);
  opacity: 0.9;
}
button.danger { color: var(--destructive); }

input, textarea, select {
  font: inherit;
  font-size: 13px;
  color: inherit;
  background: var(--background);
  border: 1px solid var(--input);
  border-radius: var(--radius-md);
  min-height: 40px;
  padding: 8px 12px;
  width: 100%;
}
textarea { min-height: 80px; }

/* コントロールのラベル。補助色 + 小さめ。 */
label {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}

a { color: var(--link); }

/*
 * ★ スクロールする場所を **.main ひとつ**に決める。
 *
 * かつては min-height: 100vh で body も伸び、.main にも overflow: auto
 * が付いていたので、スクロール可能な箱が 2 つあった。
 * 中身が入らないときに、どちらが動くかが場面で変わって落ち着かない。
 */
.app {
  display: grid;
  grid-template-columns: var(--sidebar-width) 1fr;
  height: 100%;
  overflow: hidden;
}
.sidebar {
  border-right: 1px solid var(--border);
  /* 中で送る。外（本文や body）を押し出さない。 */
  overflow: hidden;
  background: var(--sidebar);
  color: var(--sidebar-foreground);
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sidebar h1 { font-size: 15px; font-weight: 600; margin: 0 0 8px; padding: 0 4px; }
/*
 * ★ 画面が低いときに送るのは**ナビだけ**。
 * サイドバーごと送ると、ログアウトや組織の切り替えが画面外に消える。
 */
.sidebar nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.sidebar nav button {
  text-align: left;
  background: transparent;
  border-color: transparent;
  min-height: var(--nav-height);
  padding: 8px 12px;
}
.sidebar nav button[aria-current="page"] {
  background: var(--accent);
  color: var(--accent-foreground);
  border-color: var(--border);
}
.sidebar .spacer { flex: 1; }
.sidebar .who { font-size: 12px; color: var(--muted-foreground); padding: 0 4px; }

.main {
  padding: 24px 32px;
  overflow: auto;
  max-width: var(--container-max);
  width: 100%;
  /*
   * ★ grid 項目は既定で内容より縮まない（min-height: auto）。
   * 0 にしておかないと overflow: auto が効かず、外側が伸びてしまう。
   */
  min-height: 0;
  /* 高さを使い切りたい子（チャット）が flex: 1 で伸びられるように。 */
  display: flex;
  flex-direction: column;
  gap: 0;
}
/* 主アクションは右上に置く。ページ内に埋もれさせない。 */
.main > header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.main > header h2 { margin: 0; }
.main > header .spacer { flex: 1; }

/*
 * いまいる場所（テナント / プロジェクト）。
 *
 * ★ 本文の一番上に置く。サイドバーのセレクトだけだと、
 * 「入った先で自分がどこにいるか」が分からず、戻る口も見つからない。
 */
.place-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--muted);
  font-size: 13px;
}
.place-bar .sep { color: var(--muted-foreground); }
.place-bar .here { font-weight: 600; }
.place-bar .root { color: var(--muted-foreground); }
/* プロジェクトの中にいることを、色でも分かるようにする。 */
.place-bar.in-project { border-color: var(--link); }
.place-bar .spacer { flex: 1; }

/* Surface。奥行きは影だけで表現せず、必ず border を併用する。 */
.card {
  background: var(--card);
  color: var(--card-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 1px 2px 0 oklch(0 0 0 / 5%);
  padding: 16px;
}
/* セクション間は 24px、要素間は 12px（4px グリッド）。 */
.stack { display: flex; flex-direction: column; gap: 12px; }
.row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
section.stack { gap: 24px; }
.muted { color: var(--muted-foreground); }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

table { width: 100%; border-collapse: collapse; }
/* 行間を確保し、目線の走査を妨げない。 */
th, td {
  text-align: left;
  padding: 12px 12px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
  height: var(--row-height);
}
th { color: var(--muted-foreground); font-weight: 500; font-size: 12px; }

/*
 * 数字の列。
 *
 * ★ **右に寄せて桁を揃える。** 左揃えだと小数点が揃わず、比べるときに
 * 目が滑る。mono は幅は揃うが、寄せ方は直さない。
 */
.num, th.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/*
 * 長い文の列。
 *
 * ★ **切り詰めたことが見えるようにする。** 以前は文字列を 80 字で
 * 切っていたので、全文があるのかどうかが分からなかった。
 *
 * ★ **上限を置く。** 無いと長い文が他の列を押し出す。
 *
 * ★ **セルそのものには掛からない。** 自動レイアウトの表では
 * td の max-width は無視される（実測: 42ch を指定した td は 651px、
 * 内側のブロックに移すと 442px）。省略の印だけは出るので、
 * 「直った」と見えてしまう。**必ず内側の要素に置く。**
 */
.clamp {
  display: block;
  max-width: 42ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* flex 項目は既定で内容より縮まない。0 にして初めて横に送れる。 */
.table-wrap { overflow-x: auto; min-width: 0; }

/*
 * 操作列。
 *
 * ★ td 自体を flex にしない。セルが表のレイアウトから外れて、
 * 行ごとに高さも揃わなくなる（実際にガタついていた）。
 * 中に箱を 1 つ置いて、そこを flex にする。
 */
td .actions {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
  /* ★ 折り返さない。狭いときは表ごと横に送る（.table-wrap が受ける）。 */
  flex-wrap: nowrap;
}
/* 見出しも右端で揃える。ボタンが無い行があっても列幅が動かない。 */
th.actions-col, td.actions-cell { text-align: right; white-space: nowrap; width: 1%; }
/*
 * ★ 操作列は行の高さを決めない。
 *
 * セルの高さには border-bottom の 1px も入るので、上下 12px の余白のまま
 * 32px のボタンを置くと 57px になり、--row-height（56px）を 1px 超える。
 * ボタンのある行だけ背が高くなるので、この列では余白を持たせない。
 * 縦位置は td の vertical-align: middle が揃えてくれる。
 */
td.actions-cell { padding-top: 0; padding-bottom: 0; }

/*
 * 表の中のボタンは一回り小さくする。
 * 既定の 40px は行（48px）と上下 12px の余白に収まらず、行が伸びていた。
 */
td button {
  /*
   * ★ 高さを固定する。min-height だけだと行の高さ（1.75）が効いて
   * 33px になり、ボタンのある行だけ 2px 高くなる。
   * 32 + 上下の余白 12px ずつ = 56px = --row-height にぴったり収まる。
   */
  height: 32px;
  min-height: 32px;
  padding: 0 12px;
  line-height: 1;
}

.badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
}
/* ★ 実行中のバッジ。光を枠の内側に収める。 */
.badge.running { position: relative; overflow: hidden; }

/* 状態表示専用。ブランドには使わない。
   green = 完了 / amber = 保留・進行中 / red = 対応が必要。 */
.badge.accent { color: var(--link); border-color: currentColor; }
.badge.ok { color: var(--success); border-color: currentColor; }
.badge.warn { color: var(--warning); border-color: currentColor; }
.badge.danger { color: var(--destructive); border-color: currentColor; }

/*
 * ★ 高さを数えない。画面の高さから固定値を引くやり方は、
 * 上に何か足した瞬間に破綻する（パンくずを足して実際に破綻した）。
 * 親の残りを埋める形にしておけば、上が増えても勝手に合う。
 */
.chat {
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
/* grid 行も既定では縮まない。0 にして初めて中でスクロールする。 */
.messages {
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}
.message { border: 1px solid var(--border); border-radius: var(--radius); padding: 8px 12px; background: var(--card); }
.message.prompt { background: var(--muted); }
/* 認証が要るので実体は <a> ではなくボタン。見た目はリンクに寄せる。 */
.linklike {
  background: none; border: none; padding: 0; font: inherit;
  color: var(--link); text-decoration: underline; cursor: pointer;
}
.message.thinking { color: var(--muted-foreground); font-style: italic; }
.message.error { border-color: var(--destructive); color: var(--destructive); }
.message.status { color: var(--muted-foreground); font-size: 12px; padding: 2px 12px; border: none; background: transparent; }
.message .who { font-size: 12px; color: var(--muted-foreground); margin-bottom: 2px; }
.message pre { margin: 6px 0 0; padding: 8px; background: var(--muted); border-radius: 6px; overflow-x: auto; }

.composer { display: flex; gap: 8px; align-items: flex-end; }
.composer textarea { min-height: 64px; resize: vertical; }

/* Empty state。曖昧な「データがありません」で済ませない。 */
.empty { color: var(--muted-foreground); padding: 32px 24px; text-align: center; }
.error-banner { border: 1px solid var(--destructive); color: var(--destructive); border-radius: var(--radius); padding: 8px 12px; }
/* 入口。背景の点は主張させず、中身を中央に置く。 */
.login-page {
  position: relative;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.login {
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.filters > * { width: auto; }

/* 生ログ（runner.raw）の折り畳み。会話の邪魔をしない見た目にする。 */
.raw-log > summary {
  cursor: pointer;
  padding: 4px 0;
  font-size: 12px;
}
.raw-entry {
  margin: 4px 0;
  padding: 8px;
  border-radius: 6px;
  background: var(--muted);
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 220px;
  overflow: auto;
}

/* ログ行に添える付随フィールド。読めるが主役ではない見た目にする。 */
.log-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 4px;
  font-size: 11px;
  color: var(--muted-foreground);
}
.log-field {
  white-space: nowrap;
}

/* API ドキュメントの 1 行（メソッド + パス + 概要）。行ごと押せるようにする。 */
.api-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 4px 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
}
.api-row:hover {
  opacity: 0.85;
}
.api-row code {
  flex: 0 0 auto;
}

/* 添付ファイルの一覧と選択ボタン。 */
.attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.attachment-remove {
  margin-left: 6px;
  padding: 0;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: 1;
}
.attach-button {
  align-self: flex-end;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}

/* 右から出るオーバーレイ。Float（影 + border）。 */
.drawer-scrim {
  position: fixed;
  inset: 0;
  background: oklch(0 0 0 / 40%);
  display: flex;
  justify-content: flex-end;
  z-index: 50;
}
.drawer {
  background: var(--popover);
  color: var(--popover-foreground);
  border-left: 1px solid var(--border);
  box-shadow: -8px 0 24px -8px oklch(0 0 0 / 25%);
  width: min(880px, 92vw);
  height: 100vh;
  display: flex;
  flex-direction: column;
}
.drawer:focus { outline: none; }
.drawer-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border);
}
.drawer-head h3 { margin: 0; }
.drawer-head .spacer { flex: 1; }
.drawer-body {
  flex: 1;
  overflow: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* 編集欄は残りを全部使う。上限で途中で切れないようにする。 */
/* オーバーレイの中では、編集欄が残りの高さを全部使う。 */
.json-editor-fill { flex: 1; min-height: 0; display: flex; }
.drawer-body .grow { flex: 1; min-height: 160px; resize: none; }
.json-editor-fill > .cm-editor { flex: 1; min-height: 0; }
.drawer-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border);
}

/* ====================================================================
 * 装飾（Magic UI からの移植）
 *
 * ★ 動かすのは**状態を伝えるとき**だけ。実行中・読み込み中・数値の更新、
 * そして入口（ログイン画面）。一覧や表は動かさない。
 * ★ すべて prefers-reduced-motion で止まる。
 * ==================================================================== */

/* 光が枠を巡る（shine-border）。
   内側をくり抜いて枠だけ残すので、中身の描画には影響しない。 */
.shine-border {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  background-size: 300% 300%;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  will-change: background-position;
  animation-name: shine;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
@keyframes shine {
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 100%; }
  100% { background-position: 0% 0%; }
}

/* 光点が枠を走る（border-beam）。
   offset-path で辺をなぞる。motion を使わずに同じ動きになる。 */
.border-beam {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  overflow: hidden;
}
.border-beam-dot {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  filter: blur(8px);
  offset-anchor: 50% 50%;
  animation-name: beam;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
@keyframes beam {
  from { offset-distance: 0%; }
  to { offset-distance: 100%; }
}

/* 数字。桁で幅が揺れないようにする。 */
.ticker { font-variant-numeric: tabular-nums; letter-spacing: 0.01em; }

/* 文字の上を光が流れる（animated-shiny-text）。 */
.shiny-text {
  color: color-mix(in srgb, var(--muted-foreground) 70%, transparent);
  background-image: linear-gradient(
    90deg,
    transparent,
    var(--foreground),
    transparent
  );
  background-size: 120px 100%;
  background-repeat: no-repeat;
  background-clip: text;
  -webkit-background-clip: text;
  animation: shiny-text 2s linear infinite;
}
@keyframes shiny-text {
  0% { background-position: -120px 0; }
  100% { background-position: calc(100% + 120px) 0; }
}

/* 読み込み中の枠。 */
.skeleton-stack { display: flex; flex-direction: column; gap: 8px; }
.skeleton {
  border-radius: var(--radius);
  background: linear-gradient(
    90deg,
    var(--muted) 25%,
    color-mix(in srgb, var(--link) 22%, var(--muted)) 50%,
    var(--muted) 75%
  );
  background-size: 200% 100%;
  animation: skeleton 1.4s ease-in-out infinite;
}
@keyframes skeleton {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

/* ぼかしながら現れる（blur-fade）。 */
.blur-fade {
  animation: blur-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes blur-fade {
  from { opacity: 0; filter: blur(6px); transform: translateY(6px); }
  to { opacity: 1; filter: blur(0); transform: none; }
}

/* 点の背景（dot-pattern）。入口だけで使う。 */
.dot-pattern {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  fill: color-mix(in srgb, var(--foreground) 14%, transparent);
  /* 中心に向かって薄くする。文字の後ろで主張させない。 */
  -webkit-mask-image: radial-gradient(ellipse at center, transparent 10%, #000 70%);
  mask-image: radial-gradient(ellipse at center, transparent 10%, #000 70%);
}

/* 読み上げにだけ出す。 */
.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/*
 * ★ 端末が「動きを減らす」設定なら、すべて止める。
 * 消すのではなく止める——出ている情報は残す。
 */
@media (prefers-reduced-motion: reduce) {
  .shine-border,
  .border-beam-dot,
  .shiny-text,
  .skeleton,
  .blur-fade {
    animation: none !important;
  }
  .shiny-text { color: var(--muted-foreground); }
  .blur-fade { opacity: 1; filter: none; transform: none; }
}

/* ====================================================================
 * 使い方（サポートページ）
 * ★ 読み物なので、行長を抑えて目次を横に置く。
 *   密度を上げると、日本語の長文は追えなくなる。
 * ==================================================================== */

.doc-layout { display: flex; gap: 32px; align-items: flex-start; }

/* 目次。追従させるので、長い本文でも迷子にならない。 */
.doc-toc {
  position: sticky;
  top: 0;
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--muted);
  font-size: 13px;
}
.doc-toc a {
  color: var(--muted-foreground);
  text-decoration: none;
  padding: 6px 8px;
  border-radius: var(--radius);
}
.doc-toc a:hover { background: var(--accent); color: var(--accent-foreground); }

/* 本文。1 行が長すぎると読めないので上限を置く。 */
.doc-body { flex: 1 1 auto; min-width: 0; max-width: 760px; gap: 40px; }
.doc-body article { gap: 12px; }
/* アンカーで飛んだとき、見出しが上端に貼り付かないようにする。 */
.doc-body article { scroll-margin-top: 16px; }
.doc-body h3 { font-size: 16px; margin: 0; }
.doc-body h4 { font-size: 14px; margin: 12px 0 0; color: var(--muted-foreground); }
.doc-body p { margin: 0; }
.doc-body ul { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 6px; }
.doc-body code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--muted);
}

/* 補足。本文と地続きにせず、目で分けられるようにする。 */
.doc-note {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid var(--link);
  border-radius: 0 var(--radius) var(--radius) 0;
  background: color-mix(in srgb, var(--link) 8%, transparent);
  font-size: 13px;
}

/* コード例。そのまま貼れるように、コピーの口を付ける。 */
.code-block { position: relative; }
.code-block pre {
  margin: 0;
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--muted);
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.7;
}
.code-copy {
  position: absolute;
  top: 8px;
  right: 8px;
  min-height: 28px;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  /* 邪魔をしない。近づいたときだけはっきりさせる。 */
  opacity: 0.55;
}
.code-block:hover .code-copy,
.code-copy:focus-visible { opacity: 1; }

.doc-table { overflow-x: auto; min-width: 0; }
.doc-table table { font-size: 13px; }
.doc-table td, .doc-table th { padding: 10px 12px; height: auto; }

/* 横幅が足りないときは、目次を上に畳む。 */
@media (max-width: 900px) {
  .doc-layout { flex-direction: column; }
  .doc-toc { position: static; flex: none; width: 100%; flex-direction: row; flex-wrap: wrap; }
}

/* ---------------------------------------------------------- デプロイ UI */

.deploy {
  max-width: 68rem;
  margin: 0 auto;
  padding: 24px;
}

/**
 * 置き場所を選ぶ画面の、居場所の欄。
 *
 * ★ **この画面はサイドバーごと差し替わる。** ここに出さないと、
 * テナントの切り替えもログアウトも消える——置き場所を決めていない
 * テナントに入ると、そこから出られなくなる。
 */
.deploy-who {
  position: absolute;
  top: 16px;
  right: 16px;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

/* ★ 狭い画面では重ねない（選ぶ本体に被る）。 */
@media (max-width: 720px) {
  .deploy-who {
    position: static;
    align-items: stretch;
    text-align: left;
    margin-bottom: 16px;
  }
}

.deploy-shell {
  position: relative;
}

.deploy-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.deploy-manual-toggle {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--link);
}

.deploy-clouds,
.deploy-roles,
.deploy-missing,
.deploy-approvals {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.deploy-clouds button {
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  font-size: 15px;
}

.deploy-steps {
  margin: 12px 0 0;
  padding-left: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.deploy-step h4 {
  margin: 0 0 6px;
}

/*
 * 手順の「なぜ」。
 *
 * ★ 読める幅で止める。この画面の器は 68rem（1088px）なので、
 * 抑えないと 1 行 130 字を超える——いちばん読ませる画面なのに、
 * doc-body だけが 760px に抑えられていた。
 *
 * ★ ch で書く。字の大きさが変わっても字数は変わらない。
 */
.deploy-why {
  margin: 0 0 8px;
  color: var(--foreground);
  line-height: 1.7;
  max-width: 70ch;
}

/*
 * この画面の段落すべて。
 *
 * ★ **クラスではなく子孫で掛ける。** 段落は 8 か所あり、1 つずつ
 * 付けると必ず付け忘れる。こう書いておけば、段落を足した人が
 * 何もしなくても同じ幅で揃う。
 */
.deploy .doc-section p {
  max-width: 70ch;
}

.deploy-requires,
.deploy-endpoint {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 8px;
  flex-wrap: wrap;
}

.deploy-requires-label {
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--muted-foreground);
  white-space: nowrap;
}

.deploy-link {
  color: var(--link);
}

.deploy-roles li code {
  font-size: 13px;
}

.deploy-missing li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.deploy-missing strong {
  color: var(--warning);
}

.deploy-stale {
  margin: 12px 0 16px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--warning);
  color: var(--foreground);
  line-height: 1.7;
}

.deploy-stale strong {
  color: var(--warning);
}
`;
